import React, { useRef, useState } from 'react';
import {
  ActionIcon,
  Button,
  Drawer,
  FileButton,
  Flex,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { TbPlus, TbTrash, TbUpload } from 'react-icons/tb';
import type { LookupColumn, LookupColumnType, LookupDictionary } from './types';

interface DraftColumn {
  key: string;
  label: string;
  type: LookupColumnType;
}

const TYPE_OPTIONS: { value: LookupColumnType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'datetime', label: 'Date / Time' }
];

interface Props {
  idPrefix?: string;
  opened: boolean;
  onClose: () => void;
  onCreate: (dict: LookupDictionary) => Promise<unknown> | void;
  onUploadCsv?: (args: { file: File; name?: string }) => Promise<unknown> | void;
  isSubmitting?: boolean;
}

const AddDictionaryDrawer: React.FC<Props> = ({
  idPrefix,
  opened,
  onClose,
  onCreate,
  onUploadCsv,
  isSubmitting
}) => {
  const rootId = idPrefix || 'lookup-tables-drawer';
  const [mode, setMode] = useState<'manual' | 'csv'>('manual');
  const [name, setName] = useState('');
  const [columns, setColumns] = useState<DraftColumn[]>([
    { key: '', label: '', type: 'text' },
    { key: '', label: '', type: 'text' }
  ]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const resetRef = useRef<() => void>(null);

  const reset = () => {
    setMode('manual');
    setName('');
    setColumns([
      { key: '', label: '', type: 'text' },
      { key: '', label: '', type: 'text' }
    ]);
    setCsvFile(null);
    setError(null);
    resetRef.current?.();
  };

  const close = () => {
    reset();
    onClose();
  };

  const updateColumn = (idx: number, patch: Partial<DraftColumn>) => {
    setColumns(cs =>
      cs.map((c, i) => {
        if (i !== idx) return c;
        const next = { ...c, ...patch };
        // Keep the API key in sync with the label — backend uses column names
        // as object keys, so the label IS the key.
        if (patch.label !== undefined) next.key = patch.label.trim();
        return next;
      })
    );
  };

  const addColumn = () => {
    setColumns(cs => [...cs, { key: '', label: '', type: 'text' }]);
  };

  const removeColumn = (idx: number) => {
    setColumns(cs => cs.filter((_, i) => i !== idx));
  };

  const submitManual = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (columns.length === 0) {
      setError('At least one column is required');
      return;
    }
    if (columns.some(c => !c.label.trim())) {
      setError('Every column needs a name');
      return;
    }
    const labels = columns.map(c => c.label.trim());
    if (new Set(labels).size !== labels.length) {
      setError('Column names must be unique');
      return;
    }
    const finalCols: LookupColumn[] = columns.map(c => ({
      key: c.label.trim(),
      label: c.label.trim(),
      type: c.type
    }));
    const dict: LookupDictionary = {
      // Server assigns the real id; this client id is discarded after the
      // create succeeds and the list refetches.
      id: `pending-${Date.now()}`,
      name: name.trim(),
      section: 'custom',
      readonly: false,
      columns: finalCols,
      rows: []
    };
    try {
      await onCreate(dict);
      close();
    } catch {
      // Errors surfaced via toast in the parent — keep the drawer open.
    }
  };

  const submitCsv = async () => {
    if (!csvFile) {
      setError('Choose a CSV file');
      return;
    }
    if (!onUploadCsv) {
      setError('CSV upload is not available');
      return;
    }
    try {
      await onUploadCsv({ file: csvFile, name: name.trim() || undefined });
      close();
    } catch {
      // toast handled upstream
    }
  };

  return (
    <Drawer
      id={`${rootId}-root`}
      opened={opened}
      onClose={close}
      position="right"
      size={520}
      title={<Title order={4}>New Custom Dictionary</Title>}
    >
      <Stack id={`${rootId}-stack`} gap="md">
        <SegmentedControl
          id={`${rootId}-mode-toggle`}
          value={mode}
          onChange={value => {
            setMode(value as 'manual' | 'csv');
            setError(null);
          }}
          data={[
            { value: 'manual', label: 'Define columns' },
            { value: 'csv', label: 'Upload CSV' }
          ]}
        />

        <TextInput
          id={`${rootId}-name-input`}
          label="Name"
          placeholder="e.g. Asset Class to L1, L2"
          value={name}
          onChange={e => setName(e.currentTarget.value)}
          required={mode === 'manual'}
          description={mode === 'csv' ? 'Optional — defaults to the file name' : undefined}
        />

        {mode === 'manual' && (
          <div id={`${rootId}-columns-block`}>
            <Group id={`${rootId}-columns-header`} justify="space-between" mb="xs">
              <Title id={`${rootId}-columns-title`} order={6}>
                Columns
              </Title>
              <Button
                id={`${rootId}-add-column-button`}
                type="button"
                variant="subtle"
                size="xs"
                leftSection={<TbPlus size={14} />}
                onClick={addColumn}
              >
                Add column
              </Button>
            </Group>

            <Stack id={`${rootId}-columns-list`} gap="xs">
              {columns.map((col, idx) => (
                <Flex id={`${rootId}-column-row-${idx}`} key={idx} gap="xs" align="flex-end">
                  <TextInput
                    id={`${rootId}-column-name-${idx}`}
                    label={idx === 0 ? 'Name' : undefined}
                    placeholder={`Column ${idx + 1}`}
                    value={col.label}
                    onChange={e => updateColumn(idx, { label: e.currentTarget.value })}
                    style={{ flex: 1 }}
                  />
                  <Select
                    id={`${rootId}-column-type-${idx}`}
                    label={idx === 0 ? 'Type' : undefined}
                    data={TYPE_OPTIONS}
                    value={col.type}
                    onChange={v => updateColumn(idx, { type: (v as LookupColumnType) ?? 'text' })}
                    w={120}
                  />
                  <ActionIcon
                    id={`${rootId}-remove-column-${idx}`}
                    type="button"
                    variant="subtle"
                    color="red"
                    size="lg"
                    onClick={() => removeColumn(idx)}
                    disabled={columns.length <= 1}
                    aria-label={`Remove column ${idx + 1}`}
                  >
                    <TbTrash size={16} />
                  </ActionIcon>
                </Flex>
              ))}
            </Stack>
          </div>
        )}

        {mode === 'csv' && (
          <div id={`${rootId}-csv-block`}>
            <Text id={`${rootId}-csv-help`} size="sm" mb="xs" c="dimmed">
              Upload a `.csv` file. The first row must be the column names.
            </Text>
            <Group id={`${rootId}-csv-group`}>
              <FileButton
                onChange={file => {
                  setCsvFile(file);
                  setError(null);
                }}
                accept=".csv,text/csv"
                resetRef={resetRef}
              >
                {props => (
                  <Button
                    {...props}
                    id={`${rootId}-csv-choose-button`}
                    leftSection={<TbUpload size={14} />}
                    variant="outline"
                  >
                    {csvFile ? 'Replace file' : 'Choose CSV'}
                  </Button>
                )}
              </FileButton>
              {csvFile && (
                <Text id={`${rootId}-csv-filename`} size="sm" c="dimmed">
                  {csvFile.name}
                </Text>
              )}
            </Group>
          </div>
        )}

        {error && (
          <Text id={`${rootId}-error`} size="sm" c="red" role="alert">
            {error}
          </Text>
        )}

        <Group id={`${rootId}-footer`} justify="flex-end" mt="md">
          <Button
            id={`${rootId}-cancel-button`}
            variant="subtle"
            onClick={close}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            id={`${rootId}-create-button`}
            onClick={mode === 'manual' ? submitManual : submitCsv}
            loading={isSubmitting}
          >
            Create
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
};

export default AddDictionaryDrawer;
